import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
	BadgePercent,
	ChevronDown,
	Handshake,
	Loader2,
	Pencil,
	Plus,
	Trash2,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import PageTabs from "../../components/PageTabs";
import ConfirmationModal from "../../components/ConfirmationModal";
import CouponModal from "../../components/CouponModal";
import ResellerModal from "../../components/ResellerModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import useMoedaAtiva, {
	formatarMoeda,
	moedaPlataforma,
} from "../../hooks/useMoedaAtiva";

// A plataforma nem sempre cobra em real: pelo Stripe a moeda é a que o
// operador escolheu. Ver "R$" numa cobrança em euro seria mentira.
const dinheiro = (valor) => formatarMoeda(valor, moedaPlataforma());

// O mesmo cupom vale de três formas; a lista precisa dizer qual em uma linha.
const descreverCupom = (cupom) => {
	const valor = Number(cupom.amount || 0);

	const desconto =
		cupom.type === "percent"
			? `${valor}% de desconto`
			: cupom.type === "fixed"
			? `${dinheiro(valor)} de desconto`
			: `${cupom.durationMonths || valor} mês(es) grátis`;

	const duracao =
		cupom.type !== "months" && cupom.durationMonths
			? ` por ${cupom.durationMonths} mês(es)`
			: "";

	return desconto + duracao;
};

const Commercial = () => {
	// Puxa a moeda da plataforma; os formatadores acima leem dela.
	useMoedaAtiva();

	const [aba, setAba] = useState("cupons");
	const [carregando, setCarregando] = useState(true);

	const [cupons, setCupons] = useState([]);
	const [representantes, setRepresentantes] = useState([]);

	const [cupomModal, setCupomModal] = useState(false);
	const [cupomAtual, setCupomAtual] = useState(null);
	const [repModal, setRepModal] = useState(false);
	const [repAtual, setRepAtual] = useState(null);

	const [confirmar, setConfirmar] = useState(null);

	// Comissões só são buscadas quando o representante é aberto — uma consulta
	// por representante seria peso desnecessário na carga da tela.
	const [expandido, setExpandido] = useState(null);
	const [comissoes, setComissoes] = useState({});

	const carregar = useCallback(async () => {
		try {
			const [c, r] = await Promise.all([
				api.get("/coupons"),
				api.get("/resellers"),
			]);
			setCupons(c.data || []);
			setRepresentantes(r.data || []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const abrirComissoes = async (id) => {
		if (expandido === id) {
			setExpandido(null);
			return;
		}

		setExpandido(id);

		if (comissoes[id]) return;

		try {
			const { data } = await api.get(`/resellers/${id}/commissions`);
			setComissoes((atual) => ({ ...atual, [id]: data }));
		} catch (err) {
			toastError(err);
		}
	};

	const excluir = async () => {
		if (!confirmar) return;

		try {
			await api.delete(`${confirmar.rota}/${confirmar.id}`);
			toast.success(confirmar.sucesso);
			setComissoes({});
			carregar();
		} catch (err) {
			toastError(err);
		}
		setConfirmar(null);
	};

	const abas = [
		{ value: "cupons", rotulo: "Cupons", contador: cupons.length },
		{
			value: "representantes",
			rotulo: "Representantes",
			contador: representantes.length,
		},
	];

	return (
		<MainContainer>
			<div className="flex items-center gap-2 border-b px-4 py-3 sm:px-6">
				<div className="min-w-0 flex-1">
					<h1 className="text-lg font-semibold tracking-tight">Comercial</h1>
					<p className="truncate text-xs text-muted-foreground">
						Descontos que você oferece e quem vende o sistema por você
					</p>
				</div>
				<Button
					size="sm"
					className="h-9 shrink-0"
					onClick={() => {
						if (aba === "cupons") {
							setCupomAtual(null);
							setCupomModal(true);
						} else {
							setRepAtual(null);
							setRepModal(true);
						}
					}}
				>
					<Plus className="h-4 w-4" />
					<span className="hidden sm:inline">
						{aba === "cupons" ? "Novo cupom" : "Novo representante"}
					</span>
				</Button>
			</div>

			<PageTabs abas={abas} atual={aba} onChange={setAba} />

			<div className="flex-1 overflow-auto p-4 sm:p-6">
				{carregando ? (
					<div className="flex justify-center py-16">
						<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
					</div>
				) : aba === "cupons" ? (
					<div className="space-y-2">
						{cupons.length === 0 ? (
							<div className="py-16 text-center">
								<BadgePercent className="mx-auto h-8 w-8 text-muted-foreground/40" />
								<p className="mt-3 text-sm text-muted-foreground">
									Nenhum cupom criado. Crie um para dar desconto na assinatura.
								</p>
							</div>
						) : (
							cupons.map((cupom) => (
								<div
									key={cupom.id}
									className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3"
								>
									<div className="min-w-0 flex-1">
										<div className="flex flex-wrap items-center gap-2">
											<code className="rounded-md bg-muted px-2 py-0.5 text-sm font-semibold tracking-wide">
												{cupom.code}
											</code>
											<span
												className={cn(
													"rounded-full px-2 py-0.5 text-[11px] font-medium",
													cupom.isActive
														? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
														: "bg-muted text-muted-foreground"
												)}
											>
												{cupom.isActive ? "Ativo" : "Desativado"}
											</span>
										</div>
										<p className="mt-1 text-xs text-muted-foreground">
											{descreverCupom(cupom)}
											{cupom.description && ` · ${cupom.description}`}
										</p>
										<p className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
											Usado {cupom.timesUsed}
											{cupom.maxUses > 0 ? ` de ${cupom.maxUses}` : " vez(es)"}
											{cupom.validUntil &&
												` · vence em ${format(
													parseISO(cupom.validUntil),
													"dd/MM/yyyy"
												)}`}
										</p>
									</div>

									<div className="flex shrink-0 gap-1">
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8"
											aria-label={`Editar cupom ${cupom.code}`}
											onClick={() => {
												setCupomAtual(cupom.id);
												setCupomModal(true);
											}}
										>
											<Pencil className="h-4 w-4" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 text-destructive"
											aria-label={`Excluir cupom ${cupom.code}`}
											onClick={() =>
												setConfirmar({
													rota: "/coupons",
													id: cupom.id,
													titulo: `Excluir o cupom ${cupom.code}?`,
													mensagem:
														"Quem já usou continua com o desconto; ninguém mais consegue aplicá-lo.",
													sucesso: "Cupom excluído.",
												})
											}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</div>
								</div>
							))
						)}
					</div>
				) : (
					<div className="space-y-2">
						{representantes.length === 0 ? (
							<div className="py-16 text-center">
								<Handshake className="mx-auto h-8 w-8 text-muted-foreground/40" />
								<p className="mt-3 text-sm text-muted-foreground">
									Nenhum representante cadastrado. Cadastre quem revende o
									sistema para acompanhar a comissão.
								</p>
							</div>
						) : (
							representantes.map((rep) => {
								const aberto = expandido === rep.id;
								const dados = comissoes[rep.id];

								return (
									<div
										key={rep.id}
										className="overflow-hidden rounded-xl border bg-card"
									>
										<div className="flex flex-wrap items-center gap-3 p-3">
											<button
												type="button"
												className="flex min-w-0 flex-1 items-center gap-2 text-left"
												aria-expanded={aberto}
												onClick={() => abrirComissoes(rep.id)}
											>
												<ChevronDown
													className={cn(
														"h-4 w-4 shrink-0 text-muted-foreground transition-transform",
														aberto && "rotate-180"
													)}
												/>
												<div className="min-w-0">
													<div className="flex flex-wrap items-center gap-2">
														<p className="truncate text-sm font-medium">
															{rep.name}
														</p>
														{!rep.isActive && (
															<span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
																Inativo
															</span>
														)}
													</div>
													<p className="truncate text-xs text-muted-foreground">
														{Number(rep.commissionPercent)}% de comissão ·{" "}
														{rep.companies?.length || 0} empresa(s)
														{rep.email && ` · ${rep.email}`}
													</p>
												</div>
											</button>

											<div className="flex shrink-0 gap-1">
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													aria-label={`Editar representante ${rep.name}`}
													onClick={() => {
														setRepAtual(rep);
														setRepModal(true);
													}}
												>
													<Pencil className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 text-destructive"
													aria-label={`Excluir representante ${rep.name}`}
													onClick={() =>
														setConfirmar({
															rota: "/resellers",
															id: rep.id,
															titulo: `Excluir ${rep.name}?`,
															mensagem:
																"As empresas dele ficam sem representante, mas continuam ativas.",
															sucesso: "Representante excluído.",
														})
													}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										</div>

										{aberto && (
											<div className="border-t bg-muted/30 p-3">
												{!dados ? (
													<div className="flex justify-center py-4">
														<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
													</div>
												) : (
													<>
														<div className="mb-3 flex flex-wrap gap-4">
															<div>
																<p className="text-[11px] uppercase tracking-wide text-muted-foreground">
																	Pago pelas empresas
																</p>
																<p className="text-base font-semibold tabular-nums">
																	{dinheiro(dados.totalPago)}
																</p>
															</div>
															<div>
																<p className="text-[11px] uppercase tracking-wide text-muted-foreground">
																	Comissão a pagar
																</p>
																<p className="text-base font-semibold tabular-nums text-primary">
																	{dinheiro(dados.comissao)}
																</p>
															</div>
														</div>

														{dados.empresas?.length ? (
															<div className="space-y-1">
																{dados.empresas.map((empresa) => (
																	<div
																		key={empresa.companyId}
																		className="flex items-center justify-between gap-2 rounded-lg bg-card px-2.5 py-1.5"
																	>
																		<span className="min-w-0 truncate text-xs">
																			{empresa.name}
																		</span>
																		<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
																			{dinheiro(empresa.pago)} →{" "}
																			<span className="font-medium text-foreground">
																				{dinheiro(empresa.comissao)}
																			</span>
																		</span>
																	</div>
																))}
															</div>
														) : (
															<p className="text-xs text-muted-foreground">
																Nenhuma empresa vinculada ainda.
															</p>
														)}
													</>
												)}
											</div>
										)}
									</div>
								);
							})
						)}
					</div>
				)}
			</div>

			<CouponModal
				open={cupomModal}
				couponId={cupomAtual}
				onClose={() => {
					setCupomModal(false);
					setCupomAtual(null);
					carregar();
				}}
			/>

			<ResellerModal
				open={repModal}
				reseller={repAtual}
				onClose={() => {
					setRepModal(false);
					setRepAtual(null);
					setComissoes({});
					carregar();
				}}
			/>

			<ConfirmationModal
				title={confirmar?.titulo || ""}
				open={!!confirmar}
				onClose={() => setConfirmar(null)}
				onConfirm={excluir}
			>
				{confirmar?.mensagem}
			</ConfirmationModal>
		</MainContainer>
	);
};

export default Commercial;
